import _slicedToArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { DownOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMessageContent, useStreamReveal } from './hooks';
import { MessageStatus } from '../../../../ai';
import { StopMessage } from './components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var blink = keyframes(["0%,80%,100%{opacity:0;}40%{opacity:1;}"]);
var MessageRow = styled.div.withConfig({
  displayName: "messagecontainer__MessageRow",
  componentId: "sc-usavm6-0"
})(["display:flex;flex-direction:column;align-items:", ";margin-bottom:12px;"], function (p) {
  return p.$isUser ? 'flex-end' : 'flex-start';
});
var Bubble = styled.div.withConfig({
  displayName: "messagecontainer__Bubble",
  componentId: "sc-usavm6-1"
})(["max-width:80%;padding:12px 16px;border-radius:18px;font-size:14px;line-height:1.5;background-color:", ";color:", ";white-space:pre-line;word-wrap:break-word;&::selection{background:#096dd9;color:#fff;}"], function (p) {
  return p.$isUser ? '#1890ff' : '#f5f5f5';
}, function (p) {
  return p.$isUser ? '#fff' : '#333';
});
var MessageLink = styled.a.withConfig({
  displayName: "messagecontainer__MessageLink",
  componentId: "sc-usavm6-2"
})(["color:", ";text-decoration:underline;"], function (p) {
  return p.$isUser ? '#fff' : '#1677ff';
});
var DataSection = styled.div.withConfig({
  displayName: "messagecontainer__DataSection",
  componentId: "sc-usavm6-3"
})(["max-width:80%;margin-top:8px;border:1px solid #d9d9d9;border-radius:12px;background:#fafafa;overflow:hidden;"]);
var DataToggle = styled.button.withConfig({
  displayName: "messagecontainer__DataToggle",
  componentId: "sc-usavm6-4"
})(["width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border:none;background:transparent;color:#595959;font-size:13px;font-weight:600;cursor:pointer;text-align:left;"]);
var DataToggleLeft = styled.span.withConfig({
  displayName: "messagecontainer__DataToggleLeft",
  componentId: "sc-usavm6-5"
})(["display:inline-flex;align-items:center;gap:8px;"]);
var DataStatus = styled.span.withConfig({
  displayName: "messagecontainer__DataStatus",
  componentId: "sc-usavm6-6"
})(["display:inline-flex;align-items:center;gap:6px;color:#1677ff;font-size:12px;font-weight:500;"]);
var DataContent = styled.div.withConfig({
  displayName: "messagecontainer__DataContent",
  componentId: "sc-usavm6-7"
})(["padding:0 12px 12px;white-space:pre-wrap;word-break:break-word;font-size:13px;line-height:1.5;color:#434343;"]);
var StreamingCursor = styled.span.withConfig({
  displayName: "messagecontainer__StreamingCursor",
  componentId: "sc-usavm6-8"
})(["display:inline-block;width:2px;height:16px;background-color:#1890ff;margin-left:2px;animation:", " 1s infinite;vertical-align:middle;"], blink);
var TimeStamp = styled.div.withConfig({
  displayName: "messagecontainer__TimeStamp",
  componentId: "sc-usavm6-9"
})(["font-size:12px;color:#999;margin-top:4px;"]);
export var MessageContainer = function MessageContainer(_ref) {
  var message = _ref.message,
      contentOverride = _ref.contentOverride,
      dataOverride = _ref.dataOverride,
      isStreamingProp = _ref.isStreaming,
      _ref$isDataStreaming = _ref.isDataStreaming,
      isDataStreaming = _ref$isDataStreaming === void 0 ? false : _ref$isDataStreaming,
      _ref$defaultDataOpen = _ref.defaultDataOpen,
      defaultDataOpen = _ref$defaultDataOpen === void 0 ? false : _ref$defaultDataOpen,
      _ref$animateReveal = _ref.animateReveal,
      animateReveal = _ref$animateReveal === void 0 ? false : _ref$animateReveal,
      onRevealComplete = _ref.onRevealComplete;

  var _useMessageContent = useMessageContent({
    message: message,
    contentOverride: contentOverride,
    isStreaming: isStreamingProp
  }),
      isUser = _useMessageContent.isUser,
      isStreamingFromContent = _useMessageContent.isStreaming,
      displayContent = _useMessageContent.displayContent;

  var _useStreamReveal = useStreamReveal({
    content: displayContent,
    enabled: animateReveal,
    onComplete: onRevealComplete
  }),
      revealedContent = _useStreamReveal.revealedContent,
      isComplete = _useStreamReveal.isComplete;

  var isStreaming = isStreamingProp !== null && isStreamingProp !== void 0 ? isStreamingProp : isStreamingFromContent;
  var showReveal = animateReveal && !isComplete;
  var contentToShow = animateReveal ? revealedContent : displayContent;
  var dataContent = dataOverride !== null && dataOverride !== void 0 ? dataOverride : '';
  var showDataSection = !isUser && (!!dataContent || isDataStreaming);

  var _useState = useState(defaultDataOpen || isDataStreaming),
      isDataOpen = _useState[0],
      setIsDataOpen = _useState[1];

  useEffect(function () {
    if (isDataStreaming) {
      setIsDataOpen(true);
    }
  }, [isDataStreaming]);
  useEffect(function () {
    if (defaultDataOpen && showDataSection) {
      setIsDataOpen(true);
    }
  }, [defaultDataOpen, showDataSection]);

  var renderTextWithLinks = function renderTextWithLinks(text) {
    var parts = text.split(/(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s]+)/g);
    return parts.map(function (part, index) {
      var markdownLinkMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);

      if (markdownLinkMatch) {
        var _markdownLinkMatch = _slicedToArray(markdownLinkMatch, 3),
            label = _markdownLinkMatch[1],
            url = _markdownLinkMatch[2];

        return /*#__PURE__*/_jsx(MessageLink, {
          $isUser: isUser,
          href: url,
          target: "_blank",
          rel: "noreferrer",
          children: label
        }, "".concat(url, "-").concat(index));
      }

      if (/^https?:\/\/[^\s]+$/.test(part)) {
        return /*#__PURE__*/_jsx(MessageLink, {
          $isUser: isUser,
          href: part,
          target: "_blank",
          rel: "noreferrer",
          children: part
        }, "".concat(part, "-").concat(index));
      }

      return /*#__PURE__*/_jsx(React.Fragment, {
        children: part
      }, "".concat(part, "-").concat(index));
    });
  };

  return /*#__PURE__*/_jsxs(MessageRow, {
    $isUser: isUser,
    children: [!isUser && message.status === MessageStatus.Stopped ? /*#__PURE__*/_jsx(StopMessage, {}) : /*#__PURE__*/_jsxs(Bubble, {
      $isUser: isUser,
      children: [renderTextWithLinks(contentToShow), (isStreaming || showReveal) && /*#__PURE__*/_jsx(StreamingCursor, {})]
    }), showDataSection && /*#__PURE__*/_jsxs(DataSection, {
      children: [/*#__PURE__*/_jsxs(DataToggle, {
        type: "button",
        onClick: function onClick() {
          return setIsDataOpen(function (prev) {
            return !prev;
          });
        },
        children: [/*#__PURE__*/_jsxs(DataToggleLeft, {
          children: [isDataOpen ? /*#__PURE__*/_jsx(DownOutlined, {}) : /*#__PURE__*/_jsx(RightOutlined, {}), /*#__PURE__*/_jsx("span", {
            children: "Data"
          })]
        }), isDataStreaming && /*#__PURE__*/_jsxs(DataStatus, {
          children: [/*#__PURE__*/_jsx(LoadingOutlined, {}), /*#__PURE__*/_jsx("span", {
            children: "Streaming"
          })]
        })]
      }), isDataOpen && /*#__PURE__*/_jsxs(DataContent, {
        children: [dataContent, isDataStreaming && /*#__PURE__*/_jsx(StreamingCursor, {})]
      })]
    }), message.createdTime && /*#__PURE__*/_jsx(TimeStamp, {
      children: message.createdTime.toReadableDate()
    })]
  });
};